import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { AuthenticationResult } from "@azure/msal-browser";
import { CallingProcessTypes } from "csd.phoenix.models/CallingProcessTypes";
import { iid_AuthService, IAuthService } from "sensei-authentication";
import container from "../../di";
import { getConnectionFromStorage, getCurrentTenantSelection } from "../authService/storage";



export enum ContentTypes  {
  MultipartFormData = 'multipart/form-data'
}


interface IServerApiInnerError {
  type: string;
  message: string;
  stacktrace: string;
  innerError?: IServerApiInnerError;
}

export interface IServerApiError {
  error: {
    code: string;
    message: string;
    innererror?: IServerApiInnerError;
  }
}

function isServerApiError(error: any): error is IServerApiError {
  return error.error && error.error.code && error.error.message;
}


// This is returned, for example, when the WebApi fails to parse the JSON (ex. invalid Guid)
export interface IServerException {
  Message: string;
  ExceptionType: string;
  ExceptionMessage: string;
}


function isServerException(error: any): error is IServerException {
  return error.Message && error.ExceptionType && error.ExceptionMessage;
}


export class HttpError extends Error {
  readonly statusCode: number | undefined;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = "XHttpError" + (statusCode ? `.${statusCode}` : '');
    this.statusCode = statusCode;
  }
}


// Create custom instance for API reqests
const http = axios.create();

function setRequestToken(config: AxiosRequestConfig, accessToken: string) {
  console.assert(!!accessToken);
  config.headers.Authorization = `Bearer ${accessToken}`;
}

const callingProcessTypeHeaderName = "X-CallingProcessType";
const tenantInstallationUserIdHeaderName = "X-TenantInstallationUserId";
const signalRConnectionHeaderName = "X-SignalR-ConnectionId";

async function requestInterceptor(config: AxiosRequestConfig) {
  if (!config.method) {
    throw new Error("HTTP Rquest invoked witout a method.")
  }

  // Calling process
  if (!config.headers[callingProcessTypeHeaderName]) {
    config.headers[callingProcessTypeHeaderName] = CallingProcessTypes.EForms;
  }

  if (!config.headers.Authorization) {
    // publicForms/* (anonymous patient-facing form links) shares this same http instance,
    // so getToken() will throw when there is no signed-in account. Swallow the error and
    // proceed without a token -- the server enforces auth on endpoints that require it.
    try {
      const authService = container.get<IAuthService>(iid_AuthService);
      const authResponse: AuthenticationResult = await authService.getToken();
      if (authResponse?.accessToken) {
        setRequestToken(config, authResponse.accessToken);
      }
    }
    catch {
      // No authenticated account -- leave the request unauthenticated.
    }
  }

  // Tenant installation user
  if (!config.headers[tenantInstallationUserIdHeaderName]) {
    const tenantSelection = getCurrentTenantSelection();
    if (tenantSelection) {
      config.headers[tenantInstallationUserIdHeaderName] = tenantSelection.TenantInstallationUserId;
    }
  }

  if (!config.headers[signalRConnectionHeaderName]) {
    var connectionId = getConnectionFromStorage();
    if (connectionId) {
      config.headers[signalRConnectionHeaderName] = connectionId;
    }
  }
  return config;
};




function throwHttpError(error: AxiosError<any>, isRetry: boolean): never {
  const statusCode = error.response && error.response.status;
  console.assert(statusCode !== 401);
  const prefix = isRetry ? "[Retry-Phase] " : "";
  const responseData = error.response && error.response.data;

  // Handle "400 - Bad Data" error.
  if (statusCode === 400) {
    const errorResponse = error.response!.data;

    if (isServerException(errorResponse)) {
      throw new HttpError(prefix + errorResponse.ExceptionMessage, statusCode);
    }

    if (isServerApiError(errorResponse)) {
      throw new HttpError(prefix + errorResponse.error.message, statusCode);
    }

    if (errorResponse.Message) {
      throw new HttpError(prefix + errorResponse.Message, statusCode);
    }

    throw new HttpError(prefix + errorResponse, statusCode);
  }

  if (responseData) {
    if (isServerException(responseData)) {
      throw new HttpError(prefix + responseData.ExceptionMessage, statusCode);
    }

    if (isServerApiError(responseData)) {
      throw new HttpError(prefix + responseData.error.message, statusCode);
    }

    if (responseData.Message) {
      throw new HttpError(prefix + responseData.Message, statusCode);
    }
  }

  throw new HttpError(prefix + error.message, statusCode);
}

interface IRetryableRequestConfig extends AxiosRequestConfig {
  __isRetryRequest?: boolean;
}

export async function errorResponseInterceptor(error: AxiosError<any>) {
  // NOTE: When a CORS request fails, no response is passed along to Axios, and Axios will return
  // `error.code === undefined`, `error.response === undefined` and `error.message` === "Network Error".
  // Ref: https://github.com/axios/axios/issues/383
  const statusCode = error.response && error.response.status;
  if (!statusCode) {
    throw new HttpError(typeof error.code === 'undefined' ?  "Possible CORS failure." : error.message, statusCode);
  }

  // Is this an authentication error?
  if (statusCode === 401) {
    const config = error.config as IRetryableRequestConfig;

    // A 401 can mean the access token expired mid-session (e.g. a long-lived Patient
    // Mode kiosk) rather than a genuinely unauthenticated request. Try a silent
    // refresh-and-resend once before giving up, matching the retry-on-401 pattern
    // FrontEnd's interceptor already uses (FrontEnd/src/app/services/base/interceptors.ts,
    // apiResponseInterceptor's acquireToken().then(resendRequest)) -- if there's no
    // signed-in account (e.g. an anonymous publicForms/* request), getToken() throws
    // here just like it does in requestInterceptor, and we fall through to the
    // original 401 below.
    if (!config.__isRetryRequest) {
      try {
        const authService = container.get<IAuthService>(iid_AuthService);
        const authResponse: AuthenticationResult = await authService.getToken();
        if (authResponse?.accessToken) {
          config.__isRetryRequest = true;
          setRequestToken(config, authResponse.accessToken);
          return http.request(config);
        }
      } catch {
        // Refresh failed -- fall through to the authentication failure below.
      }
    }

    throw new HttpError("Authentication failure: " + error.message, statusCode);
  }

  throwHttpError(error, false);
};




http.interceptors.request.use(requestInterceptor);
http.interceptors.response.use(undefined, errorResponseInterceptor);

export default http;

